import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignupApi } from '../api';
import { errorMessage, errorStatus } from '../helpers/apiErrors';
import { useSignupStep } from '../hooks';
import { SignupAreaCode, SignupAvailableNumber } from '../types';

// The server's 409 is written for an API caller, and `errorMessage` suppresses anything that is not a
// validation error anyway, so the screen supplies its own wording.
const NUMBER_GONE = 'That number was taken a moment ago. Pick another one.';

export type SignupNumberFilter = {
  state: string;
  areaCode: string;
  city: string;
  contains: string;
};

const NO_FILTER: SignupNumberFilter = { state: '', areaCode: '', city: '', contains: '' };

export const useSignupPhoneNumberViewController = () => {
  const { companyId, funnel } = useSignupStep();
  const api = useSignupApi(companyId);
  const navigate = useNavigate();

  const [numbers, setNumbers] = useState<SignupAvailableNumber[]>([]);
  // How many match the filter overall. Telnyx offers no offset on its search, so the list is always a slice
  // and never a page that can be walked — this is what lets the screen say so instead of implying the
  // twenty on offer are everything there is.
  const [totalResults, setTotalResults] = useState<number | null>(null);
  // The screen opens by searching, so it is busy from the first frame: starting at `false` would flash the
  // empty-result notice before the first request has even left.
  const [searching, setSearching] = useState(true);
  // The filter the numbers on screen actually came from, which is not what the controls hold once the
  // visitor starts changing them. It is what lets "nothing here" name where it looked.
  const [appliedFilter, setAppliedFilter] = useState<SignupNumberFilter>(NO_FILTER);

  const [areaCodes, setAreaCodes] = useState<SignupAreaCode[]>([]);
  const [loadingAreaCodes, setLoadingAreaCodes] = useState(false);

  const [choosing, setChoosing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The number this funnel already picked, and whether Telnyx is still holding it. Without them a client who
   * comes back — a reload, a return the next day — meets a blank search, with the number they chose nowhere
   * in sight and its hold quietly rotting.
   *
   * A lapsed hold does not mean the number is lost: carrying on with it reserves it again, and only a
   * refusal from Telnyx says somebody else took it.
   */
  const chosenNumber = funnel.selectedNumber;
  const chosenHeld = funnel.numberHeld;

  // Narrowing while an earlier search is still in flight leaves two requests racing, and the slower one
  // would otherwise win and put its results back under controls that have since moved on.
  const latestSearch = useRef(0);
  const latestAreaCodes = useRef(0);

  const search = useCallback(
    async(filter: SignupNumberFilter) => {
      const request = latestSearch.current + 1;
      latestSearch.current = request;

      setSearching(true);
      setError(null);
      setAppliedFilter(filter);
      try {
        const { data } = await api.searchPhoneNumbers({
          ...(filter.state.length > 0 ? { state: filter.state } : {}),
          ...(filter.areaCode.length > 0 ? { areaCode: filter.areaCode } : {}),
          ...(filter.city.length > 0 ? { city: filter.city } : {}),
          ...(filter.contains.length > 0 ? { contains: filter.contains } : {}),
        });
        if (request !== latestSearch.current) return;
        setNumbers(data.numbers);
        setTotalResults(data.totalResults);
      } catch (e) {
        if (request !== latestSearch.current) return;
        setNumbers([]);
        setTotalResults(null);
        setError(errorMessage(e, 'Numbers could not be loaded. Please try again.'));
      } finally {
        if (request === latestSearch.current) setSearching(false);
      }
    },
    [api],
  );

  /**
   * Only codes the state actually has numbers in, each with how many. Offering the whole numbering plan
   * would fill the list with codes whose search comes back empty.
   */
  const loadAreaCodes = useCallback(
    async(state: string) => {
      const request = latestAreaCodes.current + 1;
      latestAreaCodes.current = request;

      if (state.length === 0) {
        setAreaCodes([]);
        setLoadingAreaCodes(false);
        return;
      }

      setLoadingAreaCodes(true);
      try {
        const { data } = await api.listAreaCodes(state);
        if (request !== latestAreaCodes.current) return;
        setAreaCodes(data.areaCodes);
      } catch {
        if (request !== latestAreaCodes.current) return;
        // The area code is a convenience on top of a search that works without it, so a failure here leaves
        // the picker empty rather than putting an error over a screen that is otherwise fine.
        setAreaCodes([]);
      } finally {
        if (request === latestAreaCodes.current) setLoadingAreaCodes(false);
      }
    },
    [api],
  );

  // Which company the screen has already opened for. `search` is rebuilt whenever the api object changes
  // identity, so keying the opening search on the company rather than on the effect's dependencies is what
  // keeps it to one request instead of one per render.
  const openedFor = useRef<string | null>(null);

  /**
   * Where the picker opens: the state the client gave as their own, and everything available in it. Not the
   * whole country, which is what a client asking for a number near their business reads as a shuffled list of
   * places they have nothing to do with — and not an empty list with a form either, because most visitors have
   * no opinion about their area code and should not have to answer for one before seeing anything.
   *
   * A starting point rather than a limit: "All states" sits at the top of the same control.
   */
  const openingFilter: SignupNumberFilter = useMemo(
    () => ({ ...NO_FILTER, state: funnel.companyState ?? '' }),
    [funnel.companyState],
  );

  useEffect(() => {
    if (openedFor.current === companyId) return;

    openedFor.current = companyId;
    void search(openingFilter);
    // The codes behind the opening state, so the control offers them without being touched first.
    if (openingFilter.state.length > 0) void loadAreaCodes(openingFilter.state);
  }, [companyId, loadAreaCodes, openingFilter, search]);


  const choose = useCallback(
    async(phoneNumber: string) => {
      setChoosing(phoneNumber);
      setError(null);
      try {
        await api.reservePhoneNumber(phoneNumber);
        navigate('/signup/payment');
      } catch (e) {
        if (errorStatus(e) === 409) {
          setError(NUMBER_GONE);
          // Only a 409 says the number itself is gone. Dropping it on any other failure — a timeout, a 500 —
          // would shrink the list over problems that have nothing to do with this number.
          setNumbers(current => current.filter(item => item.phoneNumber !== phoneNumber));
        } else {
          setError(errorMessage(e, 'That number could not be held. Please try again.'));
        }
      } finally {
        setChoosing(null);
      }
    },
    [api, navigate],
  );

  /**
   * Carry on with the number already chosen. A live hold goes straight to the card; a lapsed one is
   * reserved again first — the same call the picker makes for any number, failing the same way when the
   * number has been taken in the meantime.
   */
  const continueWithChosen = useCallback(async() => {
    if (chosenNumber == null) return;
    if (chosenHeld) {
      navigate('/signup/payment');
      return;
    }
    await choose(chosenNumber);
  }, [choose, chosenHeld, chosenNumber, navigate]);

  return {
    chosenNumber,
    chosenHeld,
    continueWithChosen,
    openingFilter,
    // Not a filter: the picker opens on the state, and the city is only what the field says it takes.
    companyCity: funnel.companyCity,
    numbers,
    totalResults,
    searching,
    appliedFilter,
    areaCodes,
    loadingAreaCodes,
    choosing,
    error,
    search,
    loadAreaCodes,
    choose,
  };
};
